/**
 * Pure ward layout: where every subject's mannequin stands, which way it faces, how high its
 * tier is, how it is scaled and tinted, where its visit spine and body overlays go, where a
 * subject pulled out for review stands, and where the walkable timeline lies. No three.js
 * objects here so all of it is unit tested.
 */
import type {
  BmiBucket,
  BodyRegion,
  DashboardFilters,
  Subject,
  SubjectStatus,
  Visit,
} from '@clinspace/shared';
import { PALETTE } from '../dashboards/charts.ts';
import {
  HALL,
  WARD_SITE,
  distanceToEdges,
  hallOutline,
  insidePolygon,
  wardArch,
  type Point,
} from '../world/plan.ts';

/**
 * The ward is a theatre on the east side of the hall: tiered rows on concentric arcs around a
 * focal point, every mannequin facing it, each row a step higher than the one in front. The
 * viewer at the focal point looks along +x, toward the east wall; the faces show from the
 * table and the door as well as from the ring.
 *
 * Since 2026-10-07 (the user's request) the ward stands half in the hall and half in a room of
 * its own: the focal ring and the first rows are in the hall, the rest continue through an
 * arch in the east wall into the ward room, whose side walls follow the fan's edges. The room
 * is sized from the study's subject count (`wardRoom`), so a study of 1,000 gets thirty rows
 * in a room 17.5 m deep while the hall stays as it is.
 */
export const WARD = {
  /** Where the viewer stands: every mannequin faces this point. */
  focal: WARD_SITE.focal,
  /** Unit vector from the focal point into the ward (the way the viewer faces). */
  forward: { x: 1, z: 0 },
  /** The first row's distance from the focal point and the distance between rows. */
  firstRadius: 2.4,
  rowPitch: 0.58,
  /** Each row stands this much higher than the one in front, for the first `steepRows`. */
  tierStep: 0.3,
  steepRows: 8,
  /** Beyond them each row rises less, so a ward of hundreds stays under a sane ceiling. */
  tierStepFar: 0.2,
  /** Spacing along a row, and the widest half-angle a row may span (the fan's edges). */
  slotPitch: 0.62,
  maxHalfAngle: WARD_SITE.halfAngle,
  /** Space kept between a plinth and the ward room's side walls. */
  wallClearance: 0.42,
  /** Where the wrist-menu Ward anchor puts the user (the focal point). */
  entrance: WARD_SITE.focal,
  plinthRadius: 0.26,
  plinthHeight: 0.08,
  /** A pulled-out mannequin stands this far in front of the viewer, beyond the console. */
  reviewDistance: 1.35,
  /** The filter console stands this far in front of the focal point, low and tilted back. */
  consoleDistance: 0.75,
};

/** The viewer's right when standing at the focal point facing into the ward. */
export const WARD_RIGHT = { x: -WARD.forward.z, z: WARD.forward.x };

/** Ward frame to room: `u` across (to the viewer's right), `v` forward from the focal point. */
export function wardPoint(u: number, v: number): { x: number; z: number } {
  return {
    x: WARD.focal.x + WARD_RIGHT.x * u + WARD.forward.x * v,
    z: WARD.focal.z + WARD_RIGHT.z * u + WARD.forward.z * v,
  };
}

/** A point at `angle` (radians from forward, positive to the viewer's right) and `radius`. */
export function wardPolar(angle: number, radius: number): { x: number; z: number } {
  return wardPoint(Math.sin(angle) * radius, Math.cos(angle) * radius);
}

/** Height of row `k`'s tier above the floor. */
export function tierHeight(k: number): number {
  return k < WARD.steepRows
    ? k * WARD.tierStep
    : (WARD.steepRows - 1) * WARD.tierStep + (k - WARD.steepRows + 1) * WARD.tierStepFar;
}

/** Distance from the focal point to the arch's corners, where the ward room's walls begin. */
export function archCornerRadius(): number {
  return WARD_SITE.arch / Math.cos(WARD.maxHalfAngle);
}

export interface WardSlot {
  subjectId: string;
  x: number;
  z: number;
  /** Height of the tier the plinth stands on (0 on the front row). */
  y: number;
  /** Rotation about y so the body (resting facing +z) faces the focal point (or the viewer). */
  yaw: number;
  row: number;
  column: number;
}

/** Unit vector the body faces (its +z after the yaw). */
export function forwardOf(slot: Pick<WardSlot, 'yaw'>): { x: number; z: number } {
  return { x: Math.sin(slot.yaw), z: Math.cos(slot.yaw) };
}

/** How many mannequins row `k` holds and the angle between them. */
export function rowShape(k: number): {
  radius: number;
  count: number;
  step: number;
  halfAngle: number;
} {
  const radius = WARD.firstRadius + k * WARD.rowPitch;
  // Rows that reach the ward room keep their plinths clear of its side walls (the fan's edges);
  // rows in the hall have no wall beside them.
  const walled = radius + WARD.plinthRadius > archCornerRadius() - 0.45;
  const halfAngle = walled
    ? WARD.maxHalfAngle - Math.asin(Math.min(1, WARD.wallClearance / radius))
    : WARD.maxHalfAngle;
  const count = Math.max(1, Math.floor((2 * halfAngle * radius) / WARD.slotPitch) + 1);
  const step = count > 1 ? (2 * halfAngle) / (count - 1) : 0;
  return { radius, count, step, halfAngle };
}

/** Rows needed to stand `subjects` mannequins (at least one). */
export function rowsFor(subjects: number): number {
  let k = 0;
  let total = 0;
  do total += rowShape(k++).count;
  while (total < subjects);
  return k;
}

export interface WardRoom {
  rows: number;
  /** The last row's radius, the back wall's radius, the top tier's height. */
  outerRadius: number;
  backRadius: number;
  topTier: number;
  /** Ceiling height: the hall's 6 m, or higher when the tiers climb past it. */
  height: number;
  /** The room's walls from the arch's north corner round to its south corner. */
  walls: Point[];
  /** The room's floor (the walls closed across the arch). */
  floor: Point[];
  /** The dark stone dais under the whole fan, from just behind the focal ring to the back. */
  dais: Point[];
}

/**
 * The ward room for a study of `subjects`: enough rows for all of them, a walkway behind the
 * last row, and walls along the fan's edges from the arch's corners to a curved back wall.
 */
export function wardRoom(subjects: number): WardRoom {
  const rows = rowsFor(Math.max(1, subjects));
  const outerRadius = rowShape(rows - 1).radius;
  const corner = archCornerRadius();
  const backRadius = Math.max(outerRadius + 1.6, corner + 0.6);
  const topTier = tierHeight(rows - 1);
  const height = Math.max(6, topTier + WARD.plinthHeight + 2.0 + 0.9);
  const h = WARD.maxHalfAngle;
  const fan = (radius: number, step = 0.35): Point[] => {
    const n = Math.max(6, Math.ceil((2 * h * radius) / step));
    return Array.from({ length: n + 1 }, (_, i) => wardPolar(-h + (2 * h * i) / n, radius));
  };
  const walls = [wardPolar(-h, corner), ...fan(backRadius), wardPolar(h, corner)];
  const inner = fan(1.9).reverse();
  return {
    rows,
    outerRadius,
    backRadius,
    topTier,
    height,
    walls,
    floor: walls,
    dais: [...fan(backRadius), ...inner],
  };
}

/** The hall's outline with the ward room spliced in at the arch: the whole floor. */
export function walkableOutline(room: Pick<WardRoom, 'walls'>): Point[] {
  const hall = hallOutline();
  const arch = wardArch();
  const i = hall.findIndex((p) => p.x === HALL.halfWidth && p.z === arch.z0);
  return [...hall.slice(0, i), ...room.walls, ...hall.slice(i + 2)];
}

/**
 * Whether a point is in the ward: on or near the focal ring, or among the rows (where the
 * room hides the dashboards' floor stages, which the ward's draw calls need).
 */
export function inWardArea(
  p: { x: number; z: number },
  room: Pick<WardRoom, 'backRadius'>,
): boolean {
  const dx = p.x - WARD.focal.x;
  const dz = p.z - WARD.focal.z;
  const r = Math.hypot(dx, dz);
  if (r < 2.2) return true;
  const along = dx * WARD.forward.x + dz * WARD.forward.z;
  const across = dx * WARD_RIGHT.x + dz * WARD_RIGHT.z;
  return r < room.backRadius + 0.3 && Math.abs(Math.atan2(across, along)) < WARD.maxHalfAngle + 0.2;
}

/**
 * Places subjects in site order, filling each row from the centre outward so a site's
 * subjects stand together; a partly filled odd row is offset half a step so faces show
 * between the row in front.
 */
export function layoutWard(subjects: { id: string; siteId: string }[]): WardSlot[] {
  const ordered = [...subjects].sort((a, b) =>
    a.siteId === b.siteId ? a.id.localeCompare(b.id) : a.siteId.localeCompare(b.siteId),
  );
  const slots: WardSlot[] = [];
  let k = 0;
  let i = 0;
  while (i < ordered.length) {
    const { radius, count, step } = rowShape(k);
    const n = Math.min(count, ordered.length - i);
    // Evenly spaced seats, filled from the centre outward.
    const seats = Array.from({ length: count }, (_, j) => (j - (count - 1) / 2) * step).sort(
      (a, b) => Math.abs(a) - Math.abs(b) || a - b,
    );
    for (let c = 0; c < n; c++) {
      const angle = seats[c]!;
      const { x, z } = wardPoint(Math.sin(angle) * radius, Math.cos(angle) * radius);
      slots.push({
        subjectId: ordered[i + c]!.id,
        x,
        z,
        y: tierHeight(k),
        yaw: Math.atan2(WARD.focal.x - x, WARD.focal.z - z),
        row: k,
        column: c,
      });
    }
    i += n;
    k++;
  }
  return slots;
}

/** How many subjects the first `rows` rows hold. */
export function wardCapacity(rows: number): number {
  let total = 0;
  for (let k = 0; k < rows; k++) total += rowShape(k).count;
  return total;
}

/** The slot of a mannequin pulled out for review: in front of the viewer, facing them. */
export function reviewSlot(
  subjectId: string,
  head: { x: number; z: number },
  forward: { x: number; z: number },
): WardSlot {
  const len = Math.hypot(forward.x, forward.z) || 1;
  const fx = forward.x / len;
  const fz = forward.z / len;
  const x = head.x + fx * WARD.reviewDistance;
  const z = head.z + fz * WARD.reviewDistance;
  return {
    subjectId,
    x,
    z,
    y: 0,
    yaw: Math.atan2(head.x - x, head.z - z),
    row: -1,
    column: -1,
  };
}

export interface PanelPose {
  x: number;
  y: number;
  z: number;
  /** Rotation about y; the panel's face points back at the viewer. */
  yaw: number;
  scale: number;
}

export interface ReviewArrangement {
  slot: WardSlot;
  /** One pose per page of the long record sheet, left to right, to the viewer's right. */
  sheets: PanelPose[];
  /** The swimmer-plot timeline, to the viewer's left of the mannequin. */
  swimmer: PanelPose;
  /** The profile panel, stacked above the swimmer plot. */
  profile: PanelPose;
}

/** Sizes of everything in a review, metres, and the arc they stand on. */
export const REVIEW = {
  radius: 1.85,
  patientHalfAngle: (13 * Math.PI) / 180,
  gap: (2.5 * Math.PI) / 180,
  sheet: { width: 0.9, height: 2.3 },
  /** The swimmer plot to the left of the patient and the profile stacked above it. */
  swimmer: { width: 1.4, height: 0.92, y: 1.25 },
  profile: { width: 2.4 * 0.5, height: 1.53 * 0.5, y: 2.17, scale: 0.5 },
  /** Patients out for review at once, and the space kept between their cockpits. */
  maxPatients: 3,
  margin: (4 * Math.PI) / 180,
  /** Sideways step for a cockpit that does not fit around the viewer. */
  step: 4.4,
};

/** Angular extent of a cockpit around its viewer: to the left (swimmer, profile) and right (pages). */
export function cockpitSpan(pages: number): { left: number; right: number } {
  const R = REVIEW.radius;
  const side = REVIEW.patientHalfAngle + REVIEW.gap;
  return {
    left: side + Math.max(REVIEW.swimmer.width, REVIEW.profile.width) / R,
    right: side + Math.max(1, pages) * (REVIEW.sheet.width / R + REVIEW.gap),
  };
}

export interface CockpitAnchor {
  /** Where the cockpit's viewer stands and which way they face. */
  head: { x: number; z: number };
  forward: { x: number; z: number };
  pages: number;
}

/** Bearing of a direction in the arc's convention (an angle to the right decreases it). */
function bearingOf(f: { x: number; z: number }): number {
  return Math.atan2(f.x, f.z);
}

function circularOverlap(a0: number, a1: number, b0: number, b1: number): boolean {
  for (const k of [-1, 0, 1]) {
    const o = k * 2 * Math.PI;
    if (a0 < b1 + o && b0 + o < a1) return true;
  }
  return false;
}

/**
 * Where a newly summoned patient's cockpit goes when others are already out: around the same
 * viewer, turned as little as possible from where they look so it does not overlap another
 * cockpit; when no turn fits, a step to the side (or back) clear of the others and inside the
 * hall. The first cockpit is simply where the viewer looks.
 */
export function placeCockpit(
  existing: CockpitAnchor[],
  head: { x: number; z: number },
  forward: { x: number; z: number },
  pages: number,
): CockpitAnchor {
  const len = Math.hypot(forward.x, forward.z) || 1;
  const f = { x: forward.x / len, z: forward.z / len };
  const span = cockpitSpan(pages);
  const near = existing.filter((e) => Math.hypot(e.head.x - head.x, e.head.z - head.z) < 1.5);
  const taken = near.map((e) => {
    const s = cockpitSpan(e.pages);
    const b = bearingOf(e.forward);
    return [b - s.right - REVIEW.margin, b + s.left + REVIEW.margin] as const;
  });
  const b0 = bearingOf(f);
  const step = (2 * Math.PI) / 180;
  for (let i = 0; i <= 90; i++) {
    for (const sign of i === 0 ? [1] : [1, -1]) {
      const b = b0 + sign * i * step;
      const lo = b - span.right;
      const hi = b + span.left;
      if (taken.every(([t0, t1]) => !circularOverlap(lo, hi, t0, t1)))
        return { head: { ...head }, forward: { x: Math.sin(b), z: Math.cos(b) }, pages };
    }
  }
  // No room around the viewer: step aside, away from every other cockpit, inside the hall.
  const right = { x: -f.z, z: f.x };
  const candidates = [
    { x: right.x, z: right.z },
    { x: -right.x, z: -right.z },
    { x: -f.x, z: -f.z },
    { x: right.x * 2, z: right.z * 2 },
    { x: -right.x * 2, z: -right.z * 2 },
  ].map((d) => ({ x: head.x + d.x * REVIEW.step, z: head.z + d.z * REVIEW.step }));
  const hall = hallOutline();
  const inside = (p: { x: number; z: number }) =>
    insidePolygon(p, hall) && distanceToEdges(p, hall) > 2.5;
  const clear = (p: { x: number; z: number }) =>
    existing.every((e) => Math.hypot(e.head.x - p.x, e.head.z - p.z) >= REVIEW.step - 0.1);
  const at =
    candidates.find((p) => inside(p) && clear(p)) ?? candidates.find(clear) ?? candidates[0]!;
  return { head: at, forward: f, pages };
}

/** A panel on the review arc at `angle` (radians, positive to the viewer's right). */
function arcPose(
  head: { x: number; z: number },
  f: { x: number; z: number },
  angle: number,
  y: number,
  scale: number,
): PanelPose {
  const right = { x: -f.z, z: f.x };
  const dx = f.x * Math.cos(angle) + right.x * Math.sin(angle);
  const dz = f.z * Math.cos(angle) + right.z * Math.sin(angle);
  const x = head.x + dx * REVIEW.radius;
  const z = head.z + dz * REVIEW.radius;
  return { x, y, z, yaw: Math.atan2(head.x - x, head.z - z), scale };
}

/**
 * A review cockpit around the viewer: the mannequin straight ahead, the record sheet pages to
 * its right in reading order, the swimmer plot to its left with the profile above it, all on
 * one arc and turned toward the viewer.
 */
export function reviewArrangement(
  subjectId: string,
  head: { x: number; z: number },
  forward: { x: number; z: number },
  sheetPages: number,
): ReviewArrangement {
  const len = Math.hypot(forward.x, forward.z) || 1;
  const f = { x: forward.x / len, z: forward.z / len };
  const slot = reviewSlot(subjectId, head, f);
  const R = REVIEW.radius;
  const sheets: PanelPose[] = [];
  let a = REVIEW.patientHalfAngle + REVIEW.gap;
  const sheetArc = REVIEW.sheet.width / R;
  for (let i = 0; i < sheetPages; i++) {
    sheets.push(arcPose(head, f, a + sheetArc / 2, REVIEW.sheet.height / 2 + 0.12, 1));
    a += sheetArc + REVIEW.gap;
  }
  // The swimmer plot to the left of the patient with the profile stacked above it, so a
  // cockpit takes less of the circle and two or three fit around one viewer.
  const leftArc = Math.max(REVIEW.swimmer.width, REVIEW.profile.width) / R;
  const leftAngle = -(REVIEW.patientHalfAngle + REVIEW.gap + leftArc / 2);
  return {
    slot,
    sheets,
    swimmer: arcPose(head, f, leftAngle, REVIEW.swimmer.y, 1),
    profile: arcPose(head, f, leftAngle, REVIEW.profile.y, REVIEW.profile.scale),
  };
}

/** Unit vector to the viewer's right when looking from `head` at `slot`. */
export function rightOf(
  slot: { x: number; z: number },
  head: { x: number; z: number },
): { x: number; z: number } {
  const dx = slot.x - head.x;
  const dz = slot.z - head.z;
  const len = Math.hypot(dx, dz) || 1;
  return { x: -dz / len, z: dx / len };
}

/* ---------- Patient filters ---------- */

export const AGE_BANDS = ['under 50', '50 to 64', '65 and over'] as const;
export type AgeBand = (typeof AGE_BANDS)[number];

export function ageBandOf(age: number): AgeBand {
  return age < 50 ? 'under 50' : age < 65 ? '50 to 64' : '65 and over';
}

/** The subject-level filters a reviewer can combine at the ward console. */
export function subjectPasses(
  s: Pick<Subject, 'id' | 'siteId' | 'arm' | 'sex' | 'status' | 'flags' | 'age' | 'bmiBucket'>,
  f: DashboardFilters,
): boolean {
  if (f.siteId && s.siteId !== f.siteId) return false;
  if (f.arm && s.arm !== f.arm) return false;
  if (f.subjectId && s.id !== f.subjectId) return false;
  if (f.subjectIds?.length && !f.subjectIds.includes(s.id)) return false;
  if (f.sex && s.sex !== f.sex) return false;
  if (f.status && s.status !== f.status) return false;
  if (f.flag && !(s.flags as string[]).includes(f.flag)) return false;
  if (f.ageBand && ageBandOf(s.age) !== f.ageBand) return false;
  if (f.bmiBucket && s.bmiBucket !== f.bmiBucket) return false;
  return true;
}

/* ---------- Per-subject appearance ---------- */

/** Six neutral matte tones, assigned by a hash of the id: never from any demographic field. */
export const SKIN_TONES = ['#d8c3ad', '#c9a98c', '#b48a6a', '#9a6f52', '#7a553d', '#5b3e2d'];

export function skinTone(subjectId: string): string {
  let h = 0;
  for (let i = 0; i < subjectId.length; i++) h = (h * 31 + subjectId.charCodeAt(i)) >>> 0;
  return SKIN_TONES[h % SKIN_TONES.length]!;
}

/** Width scale per BMI bucket (the three build presets, plus a slimmer one). */
export const BUILD_SCALE: Record<BmiBucket, number> = {
  Underweight: 0.9,
  Normal: 1,
  Overweight: 1.1,
  Obese: 1.22,
};

/** Instance scale for a unit-height body: height in metres, width by build. */
export function bodyScale(s: Pick<Subject, 'heightCm' | 'bmiBucket'>): {
  x: number;
  y: number;
  z: number;
} {
  const h = s.heightCm / 100;
  const w = BUILD_SCALE[s.bmiBucket];
  return { x: h * w, y: h, z: h * w };
}

export const STATUS_COLOUR: Record<SubjectStatus, string> = {
  Screening: PALETTE.navy,
  'Screen Failed': PALETTE.grey,
  Enrolled: PALETTE.tealDim,
  'On Treatment': PALETTE.teal,
  Completed: PALETTE.forest,
  Discontinued: PALETTE.amber,
  'Lost to Follow-up': PALETTE.violet,
};

/** Ring colour: status, or crimson (pulsing) after an SAE in the last 30 days. */
export function ringColour(s: Pick<Subject, 'status' | 'flags'>): {
  colour: string;
  pulse: boolean;
} {
  if (s.flags.includes('SAE last 30 days')) return { colour: PALETTE.crimson, pulse: true };
  return { colour: STATUS_COLOUR[s.status], pulse: false };
}

/* ---------- Visit spine ---------- */

export type SpineNodeKind = 'filled' | 'hollow' | 'late' | 'pending';

export interface SpineNode {
  visitId: string;
  /** Height above the plinth, metres. */
  y: number;
  kind: SpineNodeKind;
  colour: string;
}

export const SPINE = { yMin: 0.42, yMax: 2.05, radius: 0.03 };

/**
 * The spine stands beside the body on the viewer's left (the body's right hand), the side the
 * swimmer plot is on: halfway to the neighbour in the formation, a little further out for a
 * mannequin pulled out for review, and a few centimetres forward so it reads beside the arm.
 */
export const SPINE_SIDE = { formation: 0.31, review: 0.4, forward: 0.04 };

export function spineBase(
  slot: Pick<WardSlot, 'x' | 'z' | 'yaw'>,
  review = false,
): { x: number; z: number } {
  const f = forwardOf(slot);
  const side = review ? SPINE_SIDE.review : SPINE_SIDE.formation;
  return {
    x: slot.x - f.z * side + f.x * SPINE_SIDE.forward,
    z: slot.z + f.x * side + f.z * SPINE_SIDE.forward,
  };
}

/** One node per protocol visit, bottom to top in schedule order. */
export function spineNodes(
  visits: Pick<Visit, 'id' | 'status' | 'timing'>[],
  scheduleLength: number,
): SpineNode[] {
  const n = Math.max(scheduleLength, visits.length, 1);
  const step = n > 1 ? (SPINE.yMax - SPINE.yMin) / (n - 1) : 0;
  return visits.map((v, i) => {
    const y = SPINE.yMin + i * step;
    if (v.status === 'Missed') return { visitId: v.id, y, kind: 'hollow', colour: PALETTE.muted };
    if (v.status === 'Completed')
      return v.timing === 'Late' || v.timing === 'Early'
        ? { visitId: v.id, y, kind: 'late', colour: PALETTE.crimson }
        : { visitId: v.id, y, kind: 'filled', colour: PALETTE.teal };
    return { visitId: v.id, y, kind: 'pending', colour: PALETTE.grey };
  });
}

/* ---------- Body overlays ---------- */

export type Organ = 'Liver' | 'Kidney' | 'Heart' | 'Blood' | 'Pancreas';

/** Analyte to the organ region that glows; electrolytes read on the kidney. */
export const ANALYTE_ORGAN: Record<string, Organ> = {
  ALT: 'Liver',
  AST: 'Liver',
  Bilirubin: 'Liver',
  ALP: 'Liver',
  Albumin: 'Liver',
  Creatinine: 'Kidney',
  Sodium: 'Kidney',
  Potassium: 'Kidney',
  Glucose: 'Pancreas',
  Haemoglobin: 'Blood',
  WBC: 'Blood',
  Neutrophils: 'Blood',
  Lymphocytes: 'Blood',
  Platelets: 'Blood',
};

/**
 * Anchor points on a unit-height body facing +z (y up, z forward). The person's left is +x,
 * so the liver, on their right, sits at negative x.
 */
export const ORGAN_ANCHOR: Record<Organ, { x: number; y: number; z: number }> = {
  Liver: { x: -0.06, y: 0.6, z: 0.06 },
  Kidney: { x: 0.07, y: 0.57, z: -0.05 },
  Heart: { x: 0.035, y: 0.72, z: 0.06 },
  Blood: { x: 0, y: 0.68, z: 0.07 },
  Pancreas: { x: 0.03, y: 0.55, z: 0.06 },
};

export const REGION_ANCHOR: Record<BodyRegion, { x: number; y: number; z: number }> = {
  Head: { x: 0, y: 0.93, z: 0.08 },
  Chest: { x: 0, y: 0.74, z: 0.1 },
  Abdomen: { x: 0, y: 0.58, z: 0.1 },
  'Left arm': { x: 0.2, y: 0.66, z: 0.03 },
  'Right arm': { x: -0.2, y: 0.66, z: 0.03 },
  'Left leg': { x: 0.09, y: 0.33, z: 0.07 },
  'Right leg': { x: -0.09, y: 0.33, z: 0.07 },
  Skin: { x: 0.16, y: 0.84, z: 0.04 },
  Systemic: { x: 0, y: 1.06, z: 0 },
};

export function gradeColour(grade: number): string {
  if (grade >= 3) return PALETTE.crimson;
  if (grade === 2) return PALETTE.amber;
  return '#e8c26a';
}

/** Overlay point in body space (unit height, facing +z), transformed to the slot. */
export function toSlot(
  p: { x: number; y: number; z: number },
  slot: Pick<WardSlot, 'x' | 'z' | 'yaw'>,
  scale: { x: number; y: number; z: number },
): { x: number; y: number; z: number } {
  const lx = p.x * scale.x;
  const lz = p.z * scale.z;
  const cos = Math.cos(slot.yaw);
  const sin = Math.sin(slot.yaw);
  return { x: slot.x + cos * lx + sin * lz, y: p.y * scale.y, z: slot.z - sin * lx + cos * lz };
}

/* ---------- Walkable timeline ---------- */

/** The walkable timeline runs across the ward, `v` metres along forward from the focal point. */
export const TIMELINE = { length: 3, discRadius: 0.11, nearDistance: 0.26, v: -0.6 };

export interface TimelineNode {
  visitId: string;
  x: number;
  z: number;
}

/** Visit discs on a 3 m line across the ward just behind the focal point, left to right. */
export function timelineNodes(visits: Pick<Visit, 'id'>[]): TimelineNode[] {
  const n = visits.length;
  const step = n > 1 ? TIMELINE.length / (n - 1) : 0;
  return visits.map((v, i) => ({
    visitId: v.id,
    ...wardPoint(-TIMELINE.length / 2 + i * step, TIMELINE.v),
  }));
}

/** The node the viewer is standing on, if any. */
export function nearestTimelineNode(
  nodes: TimelineNode[],
  feet: { x: number; z: number },
): TimelineNode | undefined {
  let best: TimelineNode | undefined;
  let bestD = TIMELINE.nearDistance;
  for (const n of nodes) {
    const d = Math.hypot(n.x - feet.x, n.z - feet.z);
    if (d < bestD) {
      bestD = d;
      best = n;
    }
  }
  return best;
}
